import React, { useCallback, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import dndManager from '@common/utils/dnd-manager';
import AddComponentForm from '@modules/learning/forms/component/add';
import '@modules/learning/forms/component/styles.scss';
import '@modules/learning/components/components-list/styles.scss';

import { usePerformanceStore } from '@modules/performance/store/store';
import { PerformanceFormComponent, TypeAttachment } from '@modules/performance/types';
import PerformanceComponentsList from '@modules/performance/components/components-list/peformance-compoenent-list';

type Props = {
  component: PerformanceFormComponent;
};

type LearningComponent = {
  id: string;
  type: string;
  index: number;
  parameters?: Record<string, any>;
};

export const ContentModuleEditor = ({ component }: Props) => {
  const { form, drafts, updateDraft } = usePerformanceStore();

  const items: LearningComponent[] = useMemo(() => {
    const draftItems = drafts?.[component.id]?.content_components;

    const settingsItems = Array.isArray((component as any)?.settings)
      ? (component as any)?.settings
      : (component as any)?.settings?.content_components;

    const base = Array.isArray(draftItems)
      ? draftItems
      : Array.isArray(settingsItems)
      ? settingsItems
      : [];

    const normalizedItems = (base || []).map((item: any, idx: number) => {
      // Ensure every item has an ID - if missing, generate one using component ID and index
      const ensuredId = item.id || `${component.id}_${item.index ?? idx}`;

      if (!item.parameters) {
        return {
          ...item,
          id: ensuredId
        };
      }

      // Handle EXTERNAL_VIDEO separately since it doesn't have attachmentId
      if (item.type === TypeAttachment.EXTERNAL_VIDEO) {
        // Preserve the attachment object for external video
        if (item.parameters.attachment) {
          return {
            ...item,
            id: ensuredId,
            parameters: {
              ...item.parameters,
              attachment: item.parameters.attachment
            }
          };
        }
        return {
          ...item,
          id: ensuredId
        };
      }

      // Handle IMAGE, VIDEO, PDF which have attachmentId
      if ([TypeAttachment.IMAGE, TypeAttachment.VIDEO, TypeAttachment.PDF].includes(item.type)) {
        const attachmentId = item.parameters.attachmentId ||
                           item.parameters.attachment_id ||
                           item.parameters.attachment?.id;

        if (attachmentId) {
          return {
            ...item,
            id: ensuredId,
            parameters: {
              ...item.parameters,
              attachmentId,
              attachment_id: attachmentId,
              ...(item.parameters.attachment && { attachment: item.parameters.attachment })
            }
          };
        }
      }
      return {
        ...item,
        id: ensuredId
      };
    });

    return normalizedItems.filter(Boolean);
  }, [drafts, component]);

  const persist = useCallback(
    (next: LearningComponent[]) => {
      updateDraft(component.id, { content_components: next });
    },
    [component.id, updateDraft]
  );

  const handleDrag = useCallback(
    (sourceId: string, targetIndex: number) => {
      const current = [...items].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const sourceIndex = current.findIndex((c) => c.id === sourceId);
      if (sourceIndex < 0) return;
      const [moved] = current.splice(sourceIndex, 1);
      current.splice(targetIndex, 0, moved);
      const reindexed = current.map((c, idx) => ({ ...c, index: idx }));
      persist(reindexed);
    },
    [items, persist]
  );

  const handleDelete = useCallback(
    (id: string) => {
      const filtered = items.filter((c) => c.id !== id)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((c, idx) => ({ ...c, index: idx }));
      persist(filtered);
    },
    [items, persist]
  );

  const handleAdd = useCallback(
    ({ component: newComp }: { id: string; component: Omit<LearningComponent, 'id'> }) => {
      if (!component?.id || !form?.id) return;
      const maxIndex = items.reduce((max, c) => Math.max(max, c.index ?? -1), -1);
      const created: LearningComponent = {
        id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        ...newComp,
        index: (maxIndex ?? -1) + 1,
        parameters: newComp.parameters || {},
      } as any;
      persist([...(items || []), created]);
    },
    [component?.id, form?.id, items, persist]
  );

  const handleUpdateItem = useCallback(
    (screenId: string, updated: LearningComponent | null) => {
      if (screenId !== component.id) return;
      if (!updated || !(updated as any).id) return;

      const processedUpdate = { ...(updated as any) };
      if ([TypeAttachment.IMAGE, TypeAttachment.VIDEO, TypeAttachment.PDF].includes(processedUpdate.type)) {
        const existingComponent = (items || []).find((c) => c.id === processedUpdate.id);
        const existingAttachmentId = existingComponent?.parameters?.attachmentId ||
                                   existingComponent?.parameters?.attachment_id ||
                                   existingComponent?.parameters?.attachment?.id;
        const newAttachmentId = processedUpdate.parameters?.attachmentId ||
                               processedUpdate.parameters?.attachment_id ||
                               processedUpdate.parameters?.attachment?.id;

        if (existingAttachmentId || newAttachmentId) {
          processedUpdate.parameters = {
            ...processedUpdate.parameters,
            attachmentId: newAttachmentId || existingAttachmentId,
            attachment_id: newAttachmentId || existingAttachmentId,
            ...(processedUpdate.parameters?.attachment && { attachment: processedUpdate.parameters.attachment })
          };
        }
      } else if ([TypeAttachment.EXTERNAL_VIDEO].includes(processedUpdate.type)) {
        const existingComponent = (items || []).find((c) => c.id === processedUpdate.id);
        const existingAttachment = existingComponent?.parameters?.attachment;
        const newAttachment = processedUpdate.parameters?.attachment;

        if (existingAttachment || newAttachment) {
          processedUpdate.parameters = {
            ...processedUpdate.parameters,
            attachment: newAttachment || existingAttachment
          };
        }
      }

      const next = (items || []).map((c) => (c.id === processedUpdate.id ? { ...c, ...processedUpdate } : c));
      persist(next);
    },
    [component.id, items, persist]
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <DndProvider manager={dndManager}>
        <PerformanceComponentsList
          id={component.id}
          items={items || []}
          onDrag={handleDrag}
          onDelete={handleDelete}
          updateComponent={handleUpdateItem}
        />
      </DndProvider>
      <AddComponentForm
        id={component.id}
        type="screen"
        components={items || []}
        addComponent={handleAdd}
      />
    </div>
  );
};

export default ContentModuleEditor;


